import {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {debounce} from 'lodash-es'
import PropTypes from 'prop-types'
import {useDispatch, useSelector} from 'react-redux'
import {useTheme} from 'styled-components'

import {Table} from '../../../../../components/table-legacy/Table'
import {TableControllers} from '../../../../../components/table-controllers/TableControllers'
import {DropdownMenu} from '../../../../../components/dropdown-menu/DropdownMenu'
import {Spinner} from '../../../../../components/spinner-legacy/Spinner'
import {Chip} from '../../../../../components/chip/Chip'

import {AirbnbIcon, CalendarIcon, StatusIcon, UserIcon} from '../../../../../theme/icons'

import {
    errorHandler,
    formatLocaleDate,
    openLinkInNewTab,
    renderTableMatterportStatus
} from '../../../../../helpers/utils'
import {ALERT_LEVELS, MATTERPORT_STATUSES, RESPONSE_TYPES, TABLE_ALIGNMENTS} from '../../../../../helpers/constants'
import {changeSingleTableCounter, usersAutocompleteSelector} from '../../../../../store/matterport'
import {showAlert} from '../../../../../store/appGenerics'
import {httpGetMatterportTable, httpResetTask} from '../../../../../http-requests/admin/matterport.http'

import {StyledAlertIconReset} from './style'
import {useUserStore} from '../../../../../features/user/store'

export const PendingHostApprovalTable = ({tablesCounter}) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const dispatch = useDispatch()
    const adminAccessToken = useUserStore(store => store.adminAccessToken)
    const usersAutocomplete = useSelector(usersAutocompleteSelector)
    const [tableData, setTableData] = useState([])
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)
    const [sorter, setSorter] = useState({
        order_by: 'created_at',
        order_direction: 'desc'
    })
    const [search, setSearch] = useState(null)
    const [selectSearch, setSelectSearch] = useState(null)
    const [tableIsLoading, setIsTableLoading] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const tableColumns = [
        {
            name: 'external_id',
            label: t('commons:airbnb_id'),
            icon: <AirbnbIcon width={16} />,
            width: '20%',
            sortable: true
        },
        {
            name: 'reviewed_by',
            label: t('commons:reviewed_by'),
            icon: <UserIcon width={16} />,
            width: '30%',
            sortable: false,
            allowOverflow: true
        },
        {
            name: 'status',
            label: t('commons:status'),
            icon: <StatusIcon width={16} />,
            width: '25%',
            sortable: false
        },
        {
            name: 'created_at',
            label: t('commons:created_on'),
            icon: <CalendarIcon width={16} />,
            width: '15%',
            sortable: true
        },
        {
            name: 'contextMenu',
            width: '10%',
            alignment: TABLE_ALIGNMENTS.RIGHT,
            allowOverflow: true
        }
    ]

    useEffect(() => {
        if (page <= lastPage) getTableData()
    }, [page, sorter, search, selectSearch, adminAccessToken])

    useEffect(() => {
        setPage(1)
    }, [sorter, search, selectSearch])

    const getTableData = async () => {
        try {
            setIsTableLoading(true)
            const {data} = await httpGetMatterportTable(adminAccessToken, {
                status: MATTERPORT_STATUSES.PENDING_APPROVAL,
                limit: 30,
                page,
                ...(!!search && {search_by: 'code_external_id'}),
                search,
                reviewer_admin_id: selectSearch,
                response_type: RESPONSE_TYPES.EXTENDED,
                ...sorter
            })
            setTableData(page === 1 ? data.data : [...tableData, ...data.data])
            setLastPage(data?.last_page)
            dispatch(
                changeSingleTableCounter({
                    status: MATTERPORT_STATUSES.PENDING_APPROVAL,
                    count: data?.total
                })
            )
        } catch (e) {
            errorHandler(e)
        } finally {
            setIsTableLoading(false)
        }
    }

    const onSetPage = useCallback(() => {
        setPage(page + 1)
    }, [page])

    const onSortColumn = useCallback((columnName, orderDirection) => {
        setSorter({order_by: columnName, order_direction: orderDirection})
    }, [])

    const onSubmit = debounce(e => {
        const value = e?.target?.value

        if (value?.length) {
            setSearch(value)
        } else {
            setSearch(null)
        }
    }, 750)

    const onResetTask = async (event, taskId) => {
        event.stopPropagation()
        try {
            setIsLoading(true)
            await httpResetTask(adminAccessToken, taskId)
            setTableData(prevState => prevState.filter(e => e.id !== taskId))
            dispatch(
                showAlert({
                    message: t('admin:task_reset'),
                    level: ALERT_LEVELS.SUCCESS
                })
            )
            dispatch(
                changeSingleTableCounter({
                    status: MATTERPORT_STATUSES.PENDING_APPROVAL,
                    count: tablesCounter[MATTERPORT_STATUSES.PENDING_APPROVAL] - 1
                })
            )
            dispatch(
                changeSingleTableCounter({
                    status: MATTERPORT_STATUSES.UPLOAD_MEDIABOX,
                    count: tablesCounter[MATTERPORT_STATUSES.UPLOAD_MEDIABOX] + 1
                })
            )
        } catch (e) {
            errorHandler(e)
        } finally {
            setIsLoading(false)
        }
    }

    const onSelectFilterSearch = selectedOption => {
        setSelectSearch(selectedOption?.value)
    }

    return (
        <div>
            <Table
                data={tableData}
                isPaginated
                isLoading={tableIsLoading && tableData.length === 0}
                isFetchingNextPage={tableIsLoading && tableData.length >= 1}
                onChangePageCb={onSetPage}
                page={page}
                sorter={sorter}
                sortColumnCb={onSortColumn}
                columns={tableColumns}
                headerComponent={
                    <TableControllers
                        showInputSearch
                        onSearchCb={onSubmit}
                        searchInputPlaceholder={t('commons:search_airbnbid_mbid')}
                        selectFilter1={{
                            options: usersAutocomplete,
                            placeholder: t('admin:search_by_reviewer'),
                            onChange: onSelectFilterSearch
                        }}
                    />
                }
                tbodyHeight={`calc(${window.innerHeight}px - 451px)`}
                columnsRenderers={{
                    external_id: items => (
                        <span
                            style={{
                                color: theme.colors.red,
                                fontWeight: 700,
                                textDecoration: 'underline',
                                cursor: 'pointer'
                            }}
                            onClick={e => {
                                e.stopPropagation()
                                openLinkInNewTab(items.airbnb_link)
                            }}
                        >
                            {'#'}
                            {items.entity?.external_id}
                        </span>
                    ),
                    reviewed_by: items =>
                        usersAutocomplete?.find(user => user?.value === items.reviewer_admin_id)?.label || '-',
                    status: items => {
                        const {color, label} = renderTableMatterportStatus(items.status)

                        return (
                            <Chip
                                text={t(label)}
                                rounded
                                borderColor={color}
                                background={color}
                                color="white"
                                size="small"
                            />
                        )
                    },
                    created_at: items => <span>{formatLocaleDate(items.created_at)}</span>,
                    contextMenu: items => (
                        <DropdownMenu
                            id={items.id}
                            menuItems={[
                                {
                                    text: t('admin:reset').toString(),
                                    callback: event => onResetTask(event, items.id),
                                    icon: <StyledAlertIconReset size={16} />
                                }
                            ]}
                            onClick={e => e.stopPropagation()}
                        />
                    )
                }}
            />

            {isLoading && <Spinner overlayFullscreen />}
        </div>
    )
}

PendingHostApprovalTable.propTypes = {
    tablesCounter: PropTypes.shape({
        upload_to_mediabox: PropTypes.number,
        ready_for_QA: PropTypes.number,
        pending_host_approval: PropTypes.number,
        matterport_revision: PropTypes.number,
        ready_for_upload: PropTypes.number,
        completed: PropTypes.number
    })
}
